import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CalendarBlank, Trash, CopySimple, CheckSquare, Square, CalendarPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ScheduleGrid } from '../teacher/ScheduleGrid';
import { DAYS, PLANNER_DAYS, TIME_SLOTS, timeToSlotIndex } from '../teacher/constants';
import { ComparisonModal } from './ComparisonModal';

export function LessonsSection({ teachers, anno }) {
  const [teacherId, setTeacherId] = useState('');
  const scheduleGridRef = useRef(null);
  const [showComparison, setShowComparison] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [availability, setAvailability] = useState({});
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({ student_id: '', day: 0, start_time: '13:00', end_time: '13:30' });

  const [showAvailModal, setShowAvailModal] = useState(false);
  const [availDay, setAvailDay] = useState(0);
  const [disabledSlots, setDisabledSlots] = useState([]);

  // Confirm year modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const loadData = useCallback(async () => {
    if (!teacherId) return;
    setLoading(true);
    try {
      const [lessonsRes, studentsRes, availRes] = await Promise.all([
        api.getAdminTeacherLessons(teacherId, { anno }),
        api.getAdminTeacherStudents(teacherId),
        api.getAdminTeacherAvailability(teacherId),
      ]);
      setLessons(lessonsRes.data);
      setStudents(studentsRes.data.sort((a, b) => a.name.localeCompare(b.name, 'it')));
      const availMap = {};
      availRes.data.forEach(a => { availMap[a.day] = a.disabled_slots; });
      setAvailability(availMap);
    } catch { toast.error('Errore nel caricamento delle lezioni'); }
    finally { setLoading(false); }
  }, [teacherId, anno]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    api.getAdminAvailableYears?.().then(r => setAvailableYears(r.data || [])).catch(() => {});
  }, []);

  const handleCellClick = (day, time, endTime) => {
    if (!teacherId) return;
    setEditingLesson(null);
    setLessonForm({ student_id: '', day, start_time: time, end_time: endTime, orario_status: '' });
    setShowLessonModal(true);
  };
  const openEdit = (lesson) => { setEditingLesson(lesson); setLessonForm({ student_id: lesson.student_id, day: lesson.day, start_time: lesson.start_time, end_time: lesson.end_time, orario_status: lesson.orario_status || '' }); setShowLessonModal(true); };
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const { orario_status: _os, ...lessonFields } = lessonForm;
      if (editingLesson) {
        const lessonChanged = (
          editingLesson.student_id !== lessonFields.student_id ||
          editingLesson.day !== lessonFields.day ||
          editingLesson.start_time !== lessonFields.start_time ||
          editingLesson.end_time !== lessonFields.end_time
        );
        if (lessonChanged) {
          await api.updateAdminTeacherLesson(teacherId, editingLesson.id, { ...lessonFields, anno });
        }
        toast.success('Lezione aggiornata');
      } else {
        await api.createAdminTeacherLesson(teacherId, { ...lessonFields, anno });
        toast.success('Lezione creata');
      }
      setShowLessonModal(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore nel salvataggio'); }
  };

  const handleToggleStatusFlag = async (lessonId, flag, value) => {
    setLessons(prev => prev.map(l => {
      if (l.id !== lessonId) return l;
      const cur = new Set(l.status_flags || []);
      if (value) cur.add(flag); else cur.delete(flag);
      return { ...l, status_flags: ['x','i','r1','r2','r3'].filter(f => cur.has(f)) };
    }));
    try {
      await api.setLessonStatusFlag(lessonId, flag, value);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento stato');
      loadData();
    }
  };
  const handleToggleProva = async (lessonId, is_prova) => {
    setLessons(prev => prev.map(l => l.id === lessonId ? { ...l, is_prova } : l));
    try {
      await api.setLessonProva(lessonId, is_prova);
      toast.success(is_prova ? 'Flag PROVA attivato' : 'Iscrizione confermata: flag PROVA rimosso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
      loadData();
    }
  };
  const handleDelete = async (lessonId, e) => {
    if (e) e.stopPropagation();
    try { await api.deleteAdminTeacherLesson(teacherId, lessonId); toast.success('Lezione eliminata'); setShowLessonModal(false); loadData(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };
  const openAvail = (day) => { setAvailDay(day); setDisabledSlots(availability[day] || []); setShowAvailModal(true); };
  const saveAvail = async () => {
    try { await api.updateAdminTeacherAvailability(teacherId, { day: availDay, disabled_slots: disabledSlots }); toast.success('Disponibilita aggiornata'); setShowAvailModal(false); loadData(); }
    catch { toast.error("Errore nell'aggiornamento"); }
  };

  // Confirm lessons for next year
  const openConfirmYear = () => {
    setSelectedLessonIds(lessons.map(l => l.id));
    setShowConfirmModal(true);
  };
  const toggleLesson = (id) => {
    setSelectedLessonIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    if (selectedLessonIds.length === lessons.length) setSelectedLessonIds([]);
    else setSelectedLessonIds(lessons.map(l => l.id));
  };
  const handleConfirmYear = async () => {
    if (!selectedLessonIds.length) { toast.error('Seleziona almeno una lezione'); return; }
    setConfirming(true);
    try {
      const res = await api.confirmLessonsForYear({ lesson_ids: selectedLessonIds, target_year: anno + 1 });
      toast.success(`${res.data.created} lezioni confermate per ${anno + 1}${res.data.skipped ? ` (${res.data.skipped} gia esistenti)` : ''}`);
      setShowConfirmModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore nella conferma'); }
    finally { setConfirming(false); }
  };

  const teacher = teachers.find(t => t.id === teacherId);
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven'];

  const handleDuplicateLesson = async (lessonId) => {
    try {
      const res = await api.confirmLessonsForYear({ lesson_ids: [lessonId], target_year: anno + 1 });
      if (res.data.created > 0) toast.success(`Lezione duplicata nel ${anno + 1}`);
      else toast.info(`Lezione già presente nel ${anno + 1}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore duplicazione'); }
  };

  // Generate attendance from lesson — moved to Students list actions

  return (
    <>
      <div className="mb-6 animate-fade-in"><h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Orario <span className="text-[#2D2A4A] bg-[#EEF2FF] px-2 py-0.5 rounded text-lg">{anno}</span></h1><p className="text-[#52525B] text-sm">Visualizza e gestisci le lezioni di ogni insegnante con Drag & Drop</p></div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-full sm:w-[280px] rounded-sm h-10 text-sm bg-[#0A0A0A] text-white border-[#0A0A0A] font-medium [&>svg]:text-white" data-testid="admin-lessons-teacher-select"><SelectValue placeholder="Seleziona insegnante..." /></SelectTrigger>
          <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()} - {t.instrument}</SelectItem>)}</SelectContent>
        </Select>
        {teacherId && (<div className="flex gap-3 flex-wrap">
          <div className="bg-white border border-[#E4E4E7] rounded-sm px-3 py-1.5 text-sm"><span className="text-[#52525B]">Lezioni:</span> <span className="font-semibold">{lessons.length}</span></div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm px-3 py-1.5 text-sm"><span className="text-[#52525B]">Alunni:</span> <span className="font-semibold">{students.length}</span></div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm px-3 py-1.5 text-sm"><span className="text-[#52525B]">Ore/sett:</span> <span className="font-semibold">{lessons.reduce((acc, l) => acc + (timeToSlotIndex(l.end_time) - timeToSlotIndex(l.start_time)) * 0.25, 0).toFixed(1)}</span></div>
          {lessons.length > 0 && (
            <Button onClick={openConfirmYear} variant="outline" className="rounded-sm h-9 text-sm border-[#2D2A4A] text-[#2D2A4A] hover:bg-[#2D2A4A] hover:text-white" data-testid="confirm-year-btn">
              <CopySimple size={16} className="mr-1.5" />Conferma per {anno + 1}
            </Button>
          )}
        </div>)}
      </div>
      {teacherId && (
        <div className="mb-2 -mt-2 flex items-center justify-between gap-3 flex-wrap text-[11px] text-[#52525B] bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm px-3 py-1.5" data-testid="orario-status-legenda">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold uppercase text-[10px] tracking-wider text-[#2D2A4A]">Legenda stato:</span>
            <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-5 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">X</span> in attesa pagamento</span>
            <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-5 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">I</span> iscrizione pagata</span>
            <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R1</span> rata 1 pagata</span>
            <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R2</span> rata 2 pagata</span>
            <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R3</span> rata 3 pagata</span>
          </div>
          <button
            onClick={() => scheduleGridRef.current?.print()}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-[#2D2A4A] bg-white border border-[#2D2A4A] rounded-sm hover:bg-[#2D2A4A] hover:text-white transition-colors shrink-0 no-print"
            data-testid="print-schedule-button-admin"
          >
            Stampa Orario
          </button>
          <button
            onClick={() => setShowComparison(true)}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-[#2D2A4A] border border-[#2D2A4A] rounded-sm hover:bg-[#1A1830] transition-colors shrink-0 no-print"
            data-testid="compare-years-btn"
          >
            Confronta anni
          </button>
        </div>
      )}
      <div className="animate-fade-in animate-delay-2">
        {!teacherId ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm"><CalendarBlank size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Seleziona un insegnante per visualizzare le lezioni</p></div>
        ) : loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : (
          <div className="schedule-grid-wrapper"><ScheduleGrid lessons={lessons} availability={availability} onCellClick={handleCellClick} onEditLesson={openEdit} onDeleteLesson={handleDelete} onDuplicateLesson={handleDuplicateLesson} onAvailabilityClick={openAvail} loadData={loadData} updateLessonApi={(lid, data) => api.updateAdminTeacherLesson(teacherId, lid, { ...data, anno })} onToggleStatusFlag={handleToggleStatusFlag} onToggleProva={handleToggleProva} teacherName={teacher?.name} /></div>
        )}
      </div>

      <Dialog open={showLessonModal} onOpenChange={setShowLessonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">{editingLesson ? 'Modifica Lezione' : 'Nuova Lezione'}</DialogTitle><DialogDescription>{editingLesson ? 'Modifica i dettagli della lezione' : 'Inserisci i dettagli della nuova lezione'}</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label className="label-overline">Alunno</Label><Select value={lessonForm.student_id} onValueChange={(v) => setLessonForm({...lessonForm, student_id: v})}><SelectTrigger className="rounded-sm" data-testid="admin-lesson-student-select"><SelectValue placeholder="Seleziona alunno" /></SelectTrigger><SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id} className="uppercase">{s.name.toUpperCase()}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label className="label-overline">Giorno</Label><Select value={lessonForm.day.toString()} onValueChange={(v) => setLessonForm({...lessonForm, day: parseInt(v)})}><SelectTrigger className="rounded-sm" data-testid="admin-lesson-day-select"><SelectValue /></SelectTrigger><SelectContent>{PLANNER_DAYS.map((day, i) => <SelectItem key={i} value={i.toString()}>{day}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="label-overline">Ora Inizio</Label><Select value={lessonForm.start_time} onValueChange={(v) => setLessonForm({...lessonForm, start_time: v})}><SelectTrigger className="rounded-sm" data-testid="admin-lesson-start-select"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label className="label-overline">Ora Fine</Label><Select value={lessonForm.end_time} onValueChange={(v) => setLessonForm({...lessonForm, end_time: v})}><SelectTrigger className="rounded-sm" data-testid="admin-lesson-end-select"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}<SelectItem value="20:00">20:00</SelectItem></SelectContent></Select></div>
            </div>
            {editingLesson && (
              <div className="text-[10px] text-[#71717A] italic border-t border-[#E4E4E7] pt-3">
                Lo stato pagamenti (X / I / R1 / R2 / R3) si modifica cliccando sui bottoncini della cella nella griglia.
              </div>
            )}
            <DialogFooter className="flex gap-2">
              {editingLesson && <Button type="button" variant="destructive" onClick={() => handleDelete(editingLesson.id)} className="mr-auto rounded-sm" data-testid="admin-delete-lesson-btn"><Trash size={16} className="mr-2" />Elimina</Button>}
              <Button type="button" variant="outline" onClick={() => setShowLessonModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="admin-save-lesson-btn">{editingLesson ? 'Aggiorna' : 'Crea'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAvailModal} onOpenChange={setShowAvailModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Disponibilita - {DAYS[availDay]}</DialogTitle><DialogDescription>Gestisci la disponibilita per questo giorno</DialogDescription></DialogHeader>
          <div className="space-y-2"><p className="text-sm text-[#52525B] mb-2">Clicca sugli slot per disabilitarli.</p>{(() => { const dayLessons = lessons.filter(l => l.day === availDay); const occupiedSlots = new Set(); dayLessons.forEach(l => { const si = TIME_SLOTS.indexOf(l.start_time); const ei = TIME_SLOTS.indexOf(l.end_time); if (si >= 0 && ei >= 0) for (let i = si; i < ei; i++) occupiedSlots.add(TIME_SLOTS[i]); else if (si >= 0) occupiedSlots.add(TIME_SLOTS[si]); }); const freeSlots = TIME_SLOTS.filter(s => !occupiedSlots.has(s)); return (<><div className="flex flex-wrap gap-2 mb-3"><Button type="button" size="sm" className="rounded-sm text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => setDisabledSlots([...TIME_SLOTS])} data-testid="admin-disable-all-day">Disabilita Tutta la Giornata</Button><Button type="button" size="sm" className="rounded-sm text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setDisabledSlots(prev => [...new Set([...prev, ...freeSlots])])} data-testid="admin-disable-free-slots">Disabilita Slot Non Occupati</Button><Button type="button" size="sm" className="rounded-sm text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => setDisabledSlots([])} data-testid="admin-enable-all-day">Abilita Tutta la Giornata</Button></div><div className="grid grid-cols-4 gap-2">{TIME_SLOTS.map(slot => { const isOcc = occupiedSlots.has(slot); return (<button key={slot} type="button" onClick={() => setDisabledSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot])} className={`p-2 text-sm rounded border transition-colors ${disabledSlots.includes(slot) ? 'bg-red-100 text-red-700 border-red-300 font-medium' : isOcc ? 'bg-blue-50 text-blue-700 border-blue-200 font-medium' : 'bg-white text-[#0A0A0A] border-[#E4E4E7] hover:bg-[#F4F4F5]'}`} data-testid={`admin-avail-slot-${slot}`}>{slot}{isOcc ? ' *' : ''}</button>); })}</div><p className="text-[10px] text-[#A1A1AA] mt-1">* = slot con lezione</p></>); })()}</div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowAvailModal(false)} className="rounded-sm">Annulla</Button><Button onClick={saveAvail} className="bg-[#0A0A0A] rounded-sm" data-testid="admin-save-availability-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Year Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Conferma Lezioni per {anno + 1}</DialogTitle>
            <DialogDescription>Seleziona le lezioni di {teacher?.name?.toUpperCase()} da confermare per l'anno {anno + 1}. Le lezioni gia presenti nel {anno + 1} verranno saltate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <button onClick={toggleAll} className="flex items-center gap-2 text-sm font-medium text-[#2D2A4A] hover:underline" data-testid="confirm-toggle-all">
                {selectedLessonIds.length === lessons.length ? <CheckSquare size={18} /> : <Square size={18} />}
                {selectedLessonIds.length === lessons.length ? 'Deseleziona tutte' : 'Seleziona tutte'}
              </button>
              <span className="text-sm text-[#52525B]">{selectedLessonIds.length} / {lessons.length} selezionate</span>
            </div>
            <div className="border border-[#E4E4E7] rounded-sm divide-y divide-[#E4E4E7]">
              {lessons.map(l => (
                <button key={l.id} onClick={() => toggleLesson(l.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#FAFAFA] transition-colors ${selectedLessonIds.includes(l.id) ? 'bg-blue-50/50' : ''}`} data-testid={`confirm-lesson-${l.id}`}>
                  {selectedLessonIds.includes(l.id) ? <CheckSquare size={18} className="text-[#2D2A4A] shrink-0" /> : <Square size={18} className="text-[#A1A1AA] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium uppercase">{l.student_name}</span>
                  </div>
                  <span className="text-xs text-[#52525B] bg-[#F4F4F5] px-2 py-0.5 rounded">{dayNames[l.day]}</span>
                  <span className="text-xs font-['Work_Sans'] text-[#52525B]">{l.start_time}-{l.end_time}</span>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={handleConfirmYear} disabled={confirming || !selectedLessonIds.length} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="confirm-year-submit-btn">
              {confirming ? 'Conferma...' : `Conferma ${selectedLessonIds.length} lezioni per ${anno + 1}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Generate Attendance Modal — spostato nelle azioni della lista Alunni */}

      <ComparisonModal
        open={showComparison}
        onOpenChange={setShowComparison}
        teacherId={teacherId}
        teacherName={teachers.find(t => t.id === teacherId)?.name || ''}
        currentYear={anno}
        availableYears={availableYears}
      />
    </>
  );
}
